// src/services/realTimeDataService.js
import { EventEmitter } from "events";
import { marketAnalysisService } from "./marketAnalysisService";

class RealTimeDataService {
  constructor() {
    this.eventEmitter = new EventEmitter();
    this.websocket = null;
    this.activeRaces = new Map();
    this.priceData = new Map();
    this.startPrices = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.lastEventTimes = new Map(); // For event debouncing
  }
  async getHistoricalPrice(symbol, startTime) {
    try {
      // Convert symbol to lowercase and add USDT
      const ticker = `${symbol.toLowerCase()}usdt`;

      // Binance kline/candlestick API
      // Interval '1m' for minute-by-minute data
      const response = await fetch(
        `https://api.binance.com/api/v3/klines?symbol=${ticker.toUpperCase()}&interval=1m&startTime=${startTime}&limit=1`
      );

      const data = await response.json();
      if (data && data[0]) {
        // Kline data format: [openTime, open, high, low, close, volume, closeTime, ...]
        // We want the 'open' price which is index 1
        return parseFloat(data[0][1]);
      }
      return null;
    } catch (error) {
      console.error(`Error fetching historical price for ${symbol}:`, error);
      return null;
    }
  }

  // Modify startRace to use historical prices
  async startRace(raceData) {
    const raceId = raceData.id;

    // Get the start time in milliseconds
    const startTimeMs = new Date(raceData.startTime).getTime();

    // Get historical prices for each token
    await Promise.all(
      raceData.tokens.map(async (token) => {
        const historicalPrice = await this.getHistoricalPrice(
          token.symbol,
          startTimeMs
        );
        if (historicalPrice) {
          this.startPrices.set(`${raceId}-${token.symbol}`, historicalPrice);
        } else {
          // Fallback to current price if historical not available
          this.startPrices.set(
            `${raceId}-${token.symbol}`,
            this.priceData.get(token.symbol)?.price || null
          );
        }
      })
    );

    this.activeRaces.set(raceId, {
      ...raceData,
      lastUpdate: Date.now(),
    });
  }
  async getInitialPrices(symbols) {
    try {
      // Construct an array of ticker strings properly
      const tickers = symbols.map((s) => s.toUpperCase() + "USDT");

      // Encode the array as a JSON string
      const queryParam = encodeURIComponent(JSON.stringify(tickers));

      const response = await fetch(
        `https://api.binance.com/api/v3/ticker/price?symbols=${queryParam}`
      );

      const data = await response.json();

      data.forEach((item) => {
        const symbol = item.symbol.replace("USDT", "");
        this.priceData.set(symbol, {
          price: parseFloat(item.price),
          timestamp: Date.now(),
          volume: 0,
        });
      });
    } catch (error) {
      console.error("Error fetching initial prices:", error);

      // Fallback to base prices
      symbols.forEach((symbol) => {
        const basePrice =
          {
            BTC: 42000,
            ETH: 2800,
            SOL: 120,
            AVAX: 35,
            LINK: 18,
            MATIC: 1.2,
            UNI: 7.5,
            DOGE: 0.08,
            ADA: 0.5,
          }[symbol] || 100;

        this.priceData.set(symbol, {
          price: basePrice,
          timestamp: Date.now(),
          volume: 0,
        });
      });
    }
  }

  async initialize(symbols) {
    try {
      // Get initial prices first
      await this.getInitialPrices(symbols);

      const streams = symbols
        .map((symbol) => `${symbol.toLowerCase()}usdt@trade`)
        .join("/");
      const wsUrl = `wss://stream.binance.com:9443/stream?streams=${streams}`;

      this.websocket = new WebSocket(wsUrl);

      this.websocket.onopen = () => {
        console.log("WebSocket connected");
        this.reconnectAttempts = 0;
        this.eventEmitter.emit("connection", { status: "connected" });
      };

      this.websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.data) {
            this.handlePriceUpdate(data.data);
          }
        } catch (error) {
          console.error("Error processing message:", error);
          this.eventEmitter.emit("error", {
            type: "message_processing",
            error: error.message,
          });
        }
      };

      this.websocket.onerror = (error) => {
        console.error("WebSocket error:", error);
        this.eventEmitter.emit("error", {
          type: "websocket_error",
          error: error.message,
        });
      };

      this.websocket.onclose = () => {
        console.log("WebSocket disconnected");
        this.eventEmitter.emit("connection", { status: "disconnected" });
        this.handleReconnect(symbols);
      };
    } catch (error) {
      console.error("Error initializing WebSocket:", error);
      this.handleReconnect(symbols);
    }
  }

  handleReconnect(symbols) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      console.log(`Attempting to reconnect in ${delay}ms...`);
      this.eventEmitter.emit("connection", {
        status: "reconnecting",
        attempt: this.reconnectAttempts,
        delay,
      });
      setTimeout(() => this.initialize(symbols), delay);
    } else {
      this.eventEmitter.emit("connection", {
        status: "failed",
        error: "Max reconnection attempts reached",
      });
    }
  }

  getAllTokenSymbols() {
    const symbols = new Set();
    for (const race of this.activeRaces.values()) {
      race.tokens.forEach((token) => symbols.add(token.symbol));
    }
    return Array.from(symbols);
  }

  handlePriceUpdate(data) {
    if (data.e === "trade") {
      const symbol = data.s.replace("USDT", "");
      const price = parseFloat(data.p);

      // Keep the same price data structure
      this.priceData.set(symbol, {
        price,
        timestamp: data.T,
        volume: parseFloat(data.q),
      });

      // Update market analysis
      marketAnalysisService.addPricePoint(
        symbol,
        price,
        data.T,
        parseFloat(data.q)
      );

      this.updateRaces(symbol, price);
    }
  }

  updateRaces(symbol, currentPrice) {
    for (const [raceId, race] of this.activeRaces.entries()) {
      const token = race.tokens.find((t) => t.symbol === symbol);
      if (token) {
        const startPrice = this.startPrices.get(`${raceId}-${symbol}`);
        if (startPrice) {
          const oldPosition = token.position;

          // Update token data
          token.currentPrice = currentPrice;
          token.percentChange =
            ((currentPrice - startPrice) / startPrice) * 100;
          token.lastUpdate = Date.now();

          // Sort tokens by performance and update positions
          race.tokens.sort((a, b) => b.percentChange - a.percentChange);
          race.tokens.forEach((t, index) => {
            t.position = index + 1;
          });

          // Check for position changes
          if (oldPosition !== 0 && oldPosition !== token.position) {
            this.generatePositionChangeEvent(race, token, oldPosition);
          }

          // Check for other significant events
          this.checkForEvents(race, token);

          // Update race metadata
          race.lastUpdate = Date.now();

          // Emit update event
          this.eventEmitter.emit("raceUpdate", {
            raceId,
            data: race,
          });
        }
      }
    }
  }

  checkForEvents(race, token) {
    const { symbol } = token;
    const currentPrice = token.currentPrice;
    const currentVolume = this.priceData.get(symbol)?.volume || 0;

    // Check for volume spikes
    if (
      marketAnalysisService.canTriggerEvent(symbol, "volume_spike") &&
      marketAnalysisService.detectVolumeSpike(symbol, currentVolume)
    ) {
      this.generateEvent(race, {
        type: "volume_spike",
        tokens: [symbol],
        description: `${symbol} sees a significant volume increase`,
        importance: "medium",
        data: {
          volume: currentVolume,
          symbol,
        },
      });
    }

    // Check for momentum shifts
    if (
      marketAnalysisService.canTriggerEvent(symbol, "momentum_shift") &&
      marketAnalysisService.detectMomentumShift(symbol, currentPrice)
    ) {
      this.generateEvent(race, {
        type: "momentum_shift",
        tokens: [symbol],
        description: `${symbol} shows strong momentum shift`,
        importance: "high",
        data: {
          price: currentPrice,
          symbol,
          percentChange: token.percentChange,
        },
      });
    }

    // Check for support/resistance breaks
    const breakout = marketAnalysisService.detectSupportResistanceBreak(
      symbol,
      currentPrice
    );
    if (
      marketAnalysisService.canTriggerEvent(symbol, "support_level") &&
      breakout
    ) {
      this.generateEvent(race, {
        type: "support_level",
        tokens: [symbol],
        description: `${symbol} breaks ${
          breakout.type
        } level at ${breakout.level.toFixed(2)}`,
        importance: "high",
        data: {
          level: breakout.level,
          type: breakout.type,
          price: currentPrice,
          symbol,
        },
      });
    }

    // Check for milestone events
    const absPercentChange = Math.abs(token.percentChange);
    const milestone = Math.floor(absPercentChange / 5) * 5;

    if (
      milestone > 0 &&
      marketAnalysisService.canTriggerEvent(symbol, `milestone_${milestone}`)
    ) {
      this.generateEvent(race, {
        type: "milestone",
        tokens: [symbol],
        description: `${symbol} reaches ${milestone}% ${
          token.percentChange > 0 ? "gain" : "loss"
        }`,
        importance: milestone >= 10 ? "high" : "medium",
        data: {
          milestone,
          percentChange: token.percentChange,
          symbol,
        },
      });
    }
  }

  generatePositionChangeEvent(race, token, oldPosition) {
    this.generateEvent(race, {
      type: "position_change",
      tokens: [token.symbol],
      description: `${token.symbol} ${
        token.position < oldPosition ? "overtakes" : "falls behind"
      } with ${Math.abs(token.percentChange).toFixed(2)}% change`,
      importance:
        Math.abs(oldPosition - token.position) > 1 ? "high" : "medium",
      data: {
        token1: {
          symbol: token.symbol,
          percentChange: token.percentChange,
          position: token.position,
          oldPosition,
        },
      },
    });
  }

  generateEvent(race, eventData) {
    const event = {
      ...eventData,
      timestamp: Date.now(),
      formattedTime: this.formatEventTime(Date.now()),
    };

    if (!race.playByPlay) {
      race.playByPlay = [];
    }

    race.playByPlay.unshift(event);

    // Keep only last 50 events
    if (race.playByPlay.length > 50) {
      race.playByPlay.pop();
    }

    // Emit event update
    this.eventEmitter.emit("raceUpdate", {
      raceId: race.id,
      data: race,
      event, // Include the new event specifically
    });
  }

  formatEventTime(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;

    if (diff < 60000) {
      // Less than 1 minute
      return "just now";
    } else if (diff < 3600000) {
      // Less than 1 hour
      const minutes = Math.floor(diff / 60000);
      return `${minutes}m ago`;
    } else {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
  }

  subscribeToRace(raceId, callback) {
    const handler = ({ raceId: updatedRaceId, data, event }) => {
      if (raceId === updatedRaceId) {
        callback(data, event);
      }
    };

    this.eventEmitter.on("raceUpdate", handler);
    return () => this.eventEmitter.off("raceUpdate", handler);
  }

  subscribeToConnection(callback) {
    this.eventEmitter.on("connection", callback);
    return () => this.eventEmitter.off("connection", callback);
  }

  getRaceData(raceId) {
    return this.activeRaces.get(raceId);
  }

  getMarketMetrics(symbol) {
    return marketAnalysisService.getMarketMetrics(symbol);
  }

  cleanup() {
    if (this.websocket) {
      this.websocket.close();
    }
    this.eventEmitter.removeAllListeners();
    this.activeRaces.clear();
    this.priceData.clear();
    this.startPrices.clear();
    this.lastEventTimes.clear();
  }
}

export const realTimeDataService = new RealTimeDataService();
export default realTimeDataService;
